//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { getHasMultiHandleInstagramEnabled } from '@account-settings/redux/facebook-instagram/selectors/getMultiHandleFeature';
import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import { fullScreenWorkflowActions } from '@galleries/actions/fullScreenWorkflowActions';
import { requestRightsNonMentionedModalActions } from '@galleries/actions/requestRightsNonMentionedModalActions';
import { REQUEST_RIGHTS_MODAL_ACTIONS } from '@galleries/constants/galleriesConstants';
import { FULLSCREEN_WORKFLOW_NAMES } from '@galleries/models/fullScreenWorkflowModel';
import { splitMentionedItemsCombiner } from '@galleries/selectors/splitMentionedItemsCombiner';
import { ICuralateStore } from '@models/AppState';
import ItemId from '@models/ItemId';
import { createAction } from '@utils/actions';
import {
  connectionError,
  hasChromeExtension,
} from '@utils/curalate-extension-utils';
import { ReduxDispatch } from 'app/curalate.store';

const openModal = (materializedItems: IContentLibraryItem[]) => {
  return createAction({
    type: REQUEST_RIGHTS_MODAL_ACTIONS.OPEN_MODAL,
    materializedItems,
  });
};

const closeModal = () => {
  return createAction({
    type: REQUEST_RIGHTS_MODAL_ACTIONS.CLOSE_MODAL,
  });
};

const setIsChromeExtensionPresent = (extensionPresent: boolean) => {
  return createAction({
    type: REQUEST_RIGHTS_MODAL_ACTIONS.HAS_CHROME_EXTENSION,
    extensionPresent,
  });
};

const selectImage = (selectedItem: IContentLibraryItem) => {
  return createAction({
    type: REQUEST_RIGHTS_MODAL_ACTIONS.SELECT_IMAGE,
    selectedItem,
  });
};

const deselectImage = (selectedItem: IContentLibraryItem) => {
  return createAction({
    type: REQUEST_RIGHTS_MODAL_ACTIONS.DESELECT_IMAGE,
    selectedItem,
  });
};

const setItemPendingApproval = (itemId: ItemId) => {
  return createAction({
    type: REQUEST_RIGHTS_MODAL_ACTIONS.IS_PENDING_APPROVAL,
    itemId,
  });
};

const resetModal = () => {
  return createAction({
    type: REQUEST_RIGHTS_MODAL_ACTIONS.RESET_MODAL,
  });
};

export type RequestRightsModalAction = ReturnType<
  | typeof openModal
  | typeof closeModal
  | typeof setIsChromeExtensionPresent
  | typeof selectImage
  | typeof deselectImage
  | typeof setItemPendingApproval
  | typeof resetModal
>;

export const requestRightsModalActions = {
  selectImage,
  deselectImage,
  setItemPendingApproval,

  closeModal: () => (dispatch: ReduxDispatch) => {
    dispatch(
      fullScreenWorkflowActions.stopWorkflow(
        FULLSCREEN_WORKFLOW_NAMES.REQUEST_RIGHTS_AT_MENTIONED,
      ),
    );
    dispatch(closeModal());
  },

  openRequestRightsModal:
    (contentItems: IContentLibraryItem[]) =>
    (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      dispatch(resetModal());

      const defaultInstagramAccount =
        getState().instagram.account.defaultInstagramAccount;

      const connectedInstagramAccounts =
        getState().facebookInstagramSettings.connectedInstagramAccounts;

      const isMultiHandleFeatureEnabled =
        getHasMultiHandleInstagramEnabled(getState());

      const { atMentionedItems, nonAtMentionedItems } =
        splitMentionedItemsCombiner(
          contentItems,
          defaultInstagramAccount,
          connectedInstagramAccounts,
          isMultiHandleFeatureEnabled,
        );

      if (atMentionedItems.length) {
        dispatch(
          fullScreenWorkflowActions.startWorkflow(
            FULLSCREEN_WORKFLOW_NAMES.REQUEST_RIGHTS_AT_MENTIONED,
          ),
        );
        dispatch(openModal(contentItems));
        atMentionedItems.forEach((contentItem) =>
          dispatch(selectImage(contentItem)),
        );
      } else if (nonAtMentionedItems.length) {
        dispatch(
          requestRightsNonMentionedModalActions.openModal(nonAtMentionedItems),
        );
      } else if (
        (isMultiHandleFeatureEnabled &&
          !connectedInstagramAccounts?.some((account) => account.isDefault)) ||
        (!isMultiHandleFeatureEnabled && !defaultInstagramAccount)
      ) {
        dispatch(requestRightsNonMentionedModalActions.openModal(contentItems));
      }
    },

  isChromeExtensionPresent: () => (dispatch: ReduxDispatch) => {
    const ASYNC_EXTENSION_TIMEOUT = 500;
    const timeout = window.setTimeout(() => {
      dispatch(setIsChromeExtensionPresent(false));
    }, ASYNC_EXTENSION_TIMEOUT);

    hasChromeExtension((resp) => {
      window.clearTimeout(timeout);
      if (connectionError()) {
        dispatch(setIsChromeExtensionPresent(false));
      } else {
        dispatch(setIsChromeExtensionPresent(resp));
      }
    });
  },
};
